import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Star, Trophy, Flame, HelpCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface LearningSlide {
  type: 'content' | 'quiz' | 'image' | 'spot_hazard';
  title?: string;
  content?: string;
  imageUrl?: string;
  question?: string;
  quizQuestion?: string;
  options?: string[];
  quizOptions?: string[];
  correctAnswer?: number;
  quizAnswer?: number;
  explanation?: string;
  quizExplanation?: string;
  hazardAreas?: { x: number; y: number; label: string }[];
}

interface LearningModule {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty_level: string;
  xp_reward: number;
  duration_minutes: number;
  content: {
    slides: LearningSlide[];
  };
  quiz_questions: any[];
}

export default function MicroLearning() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [module, setModule] = useState<LearningModule | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [streakBonus, setStreakBonus] = useState(0);

  useEffect(() => {
    loadModule();
  }, [id]);

  const loadModule = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/signup');
        return;
      }

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('*')
        .eq('user_id', authUser.id)
        .maybeSingle();

      if (!profile) {
        navigate('/signup');
        return;
      }

      setUser({
        id: profile.id,
        displayName: profile.display_name,
        xp: profile.total_xp || 0,
        streak: profile.consecutive_safe_days || 0,
      });

      const { data: moduleData, error } = await supabase
        .from('learning_modules')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      const parsedModule = {
        ...moduleData,
        content: typeof moduleData.content === 'string' 
          ? JSON.parse(moduleData.content) 
          : moduleData.content,
        quiz_questions: typeof moduleData.quiz_questions === 'string'
          ? JSON.parse(moduleData.quiz_questions)
          : moduleData.quiz_questions
      };
      
      setModule(parsedModule as unknown as LearningModule);

      await supabase.from('worker_learning_progress').upsert({
        worker_id: profile.id,
        module_id: id,
        started_at: new Date().toISOString(),
        current_slide: 0,
        status: 'in_progress'
      }, { onConflict: 'worker_id,module_id' });

    } catch (error) {
      console.error('Error loading module:', error);
      toast.error('Gagal memuat modul pembelajaran');
    } finally {
      setLoading(false);
    }
  };

  const slides = module?.content?.slides || [];
  const totalSlides = slides.length;
  const progress = totalSlides > 0 ? ((currentSlide + 1) / totalSlides) * 100 : 0;

  const handleNext = () => {
    const slide = slides[currentSlide];
    const correctAnswer = slide?.quizAnswer ?? slide?.correctAnswer;
    
    if (slide?.type === 'quiz' && answers[currentSlide] !== correctAnswer) {
      setShowExplanation(true);
      return;
    }

    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
      setShowExplanation(false);
    } else {
      completeModule();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
      setShowExplanation(false);
    }
  };

  const completeModule = async () => {
    if (!module || !user) return;

    const quizSlides = slides.filter(s => s.type === 'quiz');
    const correctAnswers = quizSlides.filter((s) => {
      const slideIdx = slides.indexOf(s);
      const correctAnswer = s.quizAnswer ?? s.correctAnswer;
      return answers[slideIdx] === correctAnswer;
    }).length;
    
    const finalScore = quizSlides.length > 0 
      ? Math.round((correctAnswers / quizSlides.length) * 100)
      : 100;

    setScore(finalScore);

    const bonus = user.streak >= 7 ? 50 : user.streak >= 3 ? 25 : 0;
    setStreakBonus(bonus);

    try {
      // Update learning progress
      await supabase.from('worker_learning_progress').upsert({
        worker_id: user.id,
        module_id: module.id,
        completed_at: new Date().toISOString(),
        score: finalScore,
        status: 'completed',
        current_slide: totalSlides
      }, { onConflict: 'worker_id,module_id' });

      const totalXp = module.xp_reward + bonus;
      
      // Update worker XP
      await supabase.from('worker_profiles')
        .update({ total_xp: user.xp + totalXp })
        .eq('id', user.id);

      // Log XP transaction
      await supabase.from('xp_transactions').insert({
        worker_id: user.id,
        xp_amount: totalXp,
        reason: `Completed learning module: ${module.title}`
      });

      // Find and complete any associated worker_quest for this learning module
      const { data: associatedQuest } = await supabase
        .from('worker_quests')
        .select('id, status')
        .eq('worker_id', user.id)
        .or(`response_data->learning_module_id.eq.${module.id},response_data->module_id.eq.${module.id}`)
        .in('status', ['pending', 'in_progress'])
        .maybeSingle();

      if (associatedQuest) {
        console.log('Found associated quest:', associatedQuest.id);
        // Call complete-quest to properly update quest status
        const { error: completeError } = await supabase.functions.invoke('complete-quest', {
          body: { 
            questId: associatedQuest.id,
            responseData: {
              module_id: module.id,
              score: finalScore,
              completed_at: new Date().toISOString()
            }
          }
        });
        
        if (completeError) {
          console.error('Error completing quest:', completeError);
          // Fallback: directly update the quest status
          await supabase
            .from('worker_quests')
            .update({
              status: 'completed',
              completed_at: new Date().toISOString(),
              xp_awarded: totalXp
            })
            .eq('id', associatedQuest.id);
        }
      } else {
        // Check by template quest_type = 'learning' or 'micro_learning' and matching content
        const { data: learningQuests } = await supabase
          .from('worker_quests')
          .select('id, quest_templates(quest_type, content)')
          .eq('worker_id', user.id)
          .in('status', ['pending', 'in_progress']);

        const matchingQuest = learningQuests?.find((q: any) => {
          const questType = q.quest_templates?.quest_type;
          const content = q.quest_templates?.content;
          return (questType === 'learning' || questType === 'micro_learning') &&
                 content?.module_id === module.id;
        });

        if (matchingQuest) {
          console.log('Found matching learning quest:', matchingQuest.id);
          await supabase
            .from('worker_quests')
            .update({
              status: 'completed',
              completed_at: new Date().toISOString(),
              xp_awarded: totalXp
            })
            .eq('id', matchingQuest.id);
        }
      }

      setShowResult(true);
    } catch (error) {
      console.error('Error completing module:', error);
      toast.error('Gagal menyimpan progress');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!module || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Modul tidak ditemukan</p>
      </div>
    );
  }

  // Success screen
  if (showResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-sm w-full"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30"
          >
            <Trophy className="h-10 w-10 text-white" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-bold mb-2"
          >
            Modul Selesai!
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="text-muted-foreground mb-6"
          >
            {module.title}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50 p-6 mb-6 shadow-lg shadow-black/5 dark:shadow-black/20 space-y-4"
          >
            <div className="flex items-center justify-between py-2 border-b border-border/50">
              <span className="text-muted-foreground">Skor Kuis</span>
              <span className="text-xl font-bold">{score}%</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border/50">
              <span className="text-muted-foreground">XP Dasar</span>
              <span className="font-semibold text-primary">+{module.xp_reward}</span>
            </div>
            {streakBonus > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-border/50 text-amber-600 dark:text-amber-400">
                <span className="flex items-center gap-2">
                  <Flame className="h-4 w-4" />
                  Streak Bonus ({user.streak} hari)
                </span>
                <span className="font-semibold">+{streakBonus}</span>
              </div>
            )}
            <div className="pt-2">
              <div className="flex items-center justify-between">
                <span className="font-medium">Total XP</span>
                <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">+{module.xp_reward + streakBonus}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <Button onClick={() => navigate('/quests')} className="w-full rounded-xl h-12 shadow-md">
              Kembali ke Quest
            </Button>
            <Button variant="ghost" onClick={() => navigate('/home')} className="w-full rounded-xl">
              Kembali ke Home
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Progress Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-sm pt-safe">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/quests')} className="rounded-full h-9 w-9">
              <X className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <Progress value={progress} className="h-1.5" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">{currentSlide + 1}/{totalSlides}</span>
          </div>
          <div className="flex items-center justify-between">
            <Badge variant="secondary" className="capitalize bg-muted/50 font-normal shadow-sm">{module.category}</Badge>
            <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <Star className="h-4 w-4" />
              <span className="text-sm font-semibold">{module.xp_reward} XP</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
          >
            {currentSlideData?.type === 'content' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold">{currentSlideData.title}</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {currentSlideData.content}
                </p>
                {currentSlideData.imageUrl && (
                  <img 
                    src={currentSlideData.imageUrl} 
                    alt={currentSlideData.title} 
                    className="w-full rounded-2xl shadow-lg shadow-black/10 dark:shadow-black/30"
                  />
                )}
              </div>
            )}

            {currentSlideData?.type === 'image' && (
              <div className="space-y-4">
                <img 
                  src={currentSlideData.imageUrl || ''} 
                  alt={currentSlideData.title} 
                  className="w-full h-64 object-cover rounded-2xl shadow-lg shadow-black/10 dark:shadow-black/30"
                />
                <h2 className="text-lg font-bold">{currentSlideData.title}</h2>
                <p className="text-sm text-muted-foreground">{currentSlideData.content}</p>
              </div>
            )}

            {currentSlideData?.type === 'quiz' && (() => {
              const quizQuestion = currentSlideData.quizQuestion || currentSlideData.question;
              const quizOptions = currentSlideData.quizOptions || currentSlideData.options || [];
              const correctAnswer = currentSlideData.quizAnswer ?? currentSlideData.correctAnswer;
              const explanation = currentSlideData.quizExplanation || currentSlideData.explanation;
              
              return (
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shadow-sm">
                      <HelpCircle className="h-4 w-4 text-primary" />
                    </div>
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-0 shadow-sm">Kuis</Badge>
                  </div>
                  
                  <h2 className="text-lg font-semibold leading-relaxed">{quizQuestion}</h2>
                  
                  <RadioGroup
                    value={answers[currentSlide]?.toString() || ''}
                    onValueChange={(value) => setAnswers(prev => ({ ...prev, [currentSlide]: parseInt(value) }))}
                    className="space-y-3"
                  >
                    {quizOptions.map((option, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center space-x-3 p-4 rounded-xl border transition-all shadow-sm ${
                          showExplanation 
                            ? idx === correctAnswer 
                              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 shadow-emerald-500/10' 
                              : answers[currentSlide] === idx 
                                ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/30 shadow-rose-500/10'
                                : 'border-border/50'
                            : answers[currentSlide] === idx 
                              ? 'border-primary bg-primary/5 shadow-primary/10' 
                              : 'border-border/50 hover:border-border'
                        }`}
                      >
                        <RadioGroupItem value={idx.toString()} id={`option-${idx}`} disabled={showExplanation} />
                        <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer text-sm">
                          {option}
                        </Label>
                        {showExplanation && idx === correctAnswer && (
                          <CheckCircle className="h-5 w-5 text-emerald-500" />
                        )}
                      </div>
                    ))}
                  </RadioGroup>

                  {showExplanation && explanation && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50 shadow-md shadow-black/5 dark:shadow-black/20"
                    >
                      <p className="text-sm font-medium mb-1">Penjelasan:</p>
                      <p className="text-sm text-muted-foreground">{explanation}</p>
                    </motion.div>
                  )}
                </div>
              );
            })()}

            {currentSlideData?.type === 'spot_hazard' && (
              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-black/10 dark:shadow-black/30">
                  <img 
                    src={currentSlideData.imageUrl || ''} 
                    alt="Spot the hazard" 
                    className="w-full"
                  />
                  {currentSlideData.hazardAreas?.map((area, idx) => (
                    <div
                      key={idx}
                      className="absolute w-8 h-8 rounded-full border-4 border-rose-500 bg-rose-500/20 animate-pulse"
                      style={{ left: `${area.x}%`, top: `${area.y}%`, transform: 'translate(-50%, -50%)' }}
                    />
                  ))}
                </div>
                <h2 className="text-lg font-bold">Temukan Bahayanya!</h2>
                <p className="text-sm text-muted-foreground">{currentSlideData.content}</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-xl border-t border-border/50 shadow-lg shadow-black/5">
        <div className="max-w-2xl mx-auto flex gap-3">
          {currentSlide > 0 && (
            <Button variant="outline" onClick={handlePrev} className="flex-1 h-12 rounded-xl shadow-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Sebelumnya
            </Button>
          )}
          <Button 
            onClick={handleNext} 
            className="flex-1 h-12 rounded-xl shadow-md"
            disabled={currentSlideData?.type === 'quiz' && answers[currentSlide] === undefined}
          >
            {currentSlide === totalSlides - 1 ? (
              <>
                Selesai
                <CheckCircle className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                Lanjut
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}